import React, { ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { GetWine_wine } from "../../../types/__generated__/GetWine";
import {
  I18N_NAMESPACES,
  GHOST_PRODUCTION_METHOD_CARD_TITLE,
  WINE_CLASS_IDS,
  WINE_OAK_LEVEL,
  GHOST_IMAGE_SIZE,
  GHOST_PRODUCTION_METHOD_IMAGE_SIZE,
} from "../../../helpers/constants";
import { executeGhostApi, generateImageUrl } from "../../../helpers/ghost";
import { logError } from "../../../helpers/logging";
import { checkIfWineTypeIsOrangeWine } from "../../../helpers/tools";
import { colors } from "../../../styles/variables";
import URL_PATTERNS from "../../../urls";
import blackUmbrella from "../../../assets/images/general/black-umbrella-faded.png";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import GhostArticlePostCard from "../../atoms/GhostArticlePostCard";
import ArrowNavigator from "../../atoms/ArrowNavigator";

import { CardsContainer, ContentContainer, Header, NavigationBarWrapper, SeeAllLink } from "./style";

import {
  WINE_DETAIL_GUIDE_CARD_TAG_COLOR,
  WINE_DETAIL_GUIDE_PRIMARY_TAGS,
  WINE_GUIDE_SLUGS,
  PRODUCTION_METHOD_NAMES,
} from "./helper";

interface GuidesArticlePost {
  canonical_url?: string | undefined;
  url?: string | undefined;
  title: string;
  tags?: string[] | null | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  primary_tag?: any;
  feature_image?: string | null | undefined;
  reading_time?: number | null | undefined;
  slug?: string | null | undefined;
  cardTitle?: string | null | undefined;
}

interface GuidesRelatedToWineSectionProps {
  wine: GetWine_wine | null | undefined;
  isWineDetailsPage: boolean;
}

const GuidesRelatedToWineSection = ({ wine, isWineDetailsPage }: GuidesRelatedToWineSectionProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.WINE_PAGES, I18N_NAMESPACES.COMMON]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [guideArticles, setArticles] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean | null>(null);
  const horizontalContainer = useRef<HTMLDivElement | null>(null);

  const wineTypeId = wine?.wineType?.id ?? 0;
  const wineClassId = wine?.wineType?.wineClass?.id;

  const isWineTypeOrange = checkIfWineTypeIsOrangeWine(wineTypeId);

  useEffect(() => {
    const fetchAndStoreGuideArticles = async () => {
      const filters = Object.values(WINE_DETAIL_GUIDE_PRIMARY_TAGS);
      try {
        setIsLoading(true);
        const articles = await executeGhostApi(filters);
        setArticles(articles);
        setIsLoading(false);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        logError(error);
        setIsLoading(false);
      }
    };

    fetchAndStoreGuideArticles();
  }, []);

  const filterGuideArticles = (): GuidesArticlePost[] => {
    const wineClassName = wine?.wineType?.wineClass?.name; // Eg. Red, White, Orange..
    const wineTypeName = wine?.wineType?.name; // Eg. Grape: Shiraz..
    const wineCountryName = wine?.country?.name; // Eg: Australia, France...
    const wineOakLevel = wine?.wineOaky?.level;
    const wineProductionMethods = wine?.wineProductionMethods;

    const filteredArticles: GuidesArticlePost[] = [];

    guideArticles?.forEach((post: GuidesArticlePost) => {
      if (
        post &&
        post?.canonical_url && // filters posts w/o seo metadata
        post?.tags?.length === 1 // filters glossary posts
      ) {
        const postTitle = post?.title?.toLowerCase();

        let cardTitle = null;

        // Wine Class post. Also for Orange Wine,
        // wine class filter is not applied since orange wines have white wine class in our db.
        const isWineClass =
          wineClassName && !isWineTypeOrange && postTitle.includes(wineClassName.toLowerCase());
        if (isWineClass) {
          cardTitle = t("winePages:wineDetailsCardTitle.wine101");
        }

        // A more stricter filter rule to avoid showing 'Grenache blanc' post on Grenache grape types.
        const isWineType = wineTypeName && wineTypeName.toLowerCase() === postTitle;
        if (isWineType) {
          cardTitle = t("winePages:wineDetailsCardTitle.wineType");
        }

        const isWineCountry = wineCountryName && postTitle.includes(wineCountryName.toLowerCase());
        if (isWineCountry) {
          cardTitle = t("winePages:wineDetailsCardTitle.region");
        }

        const hasWineProductionMethod = !!wineProductionMethods?.some(
          (productionMethod) =>
            productionMethod?.name &&
            (postTitle.includes(productionMethod?.name?.toLowerCase()) ||
              postTitle.includes(productionMethod?.name?.split("/")?.[0]?.toLowerCase()))
        );
        if (hasWineProductionMethod) {
          cardTitle = t("winePages:wineDetailsCardTitle.prodMethods");
        }

        // Shows guide posts if the post title includes:
        // wine class, wine type, wine country or wine production method name.
        if (isWineClass || isWineType || isWineCountry || hasWineProductionMethod) {
          filteredArticles.push({ ...post, cardTitle });
        }

        // Wine Making Technique guide is shown for
        // Orange wine types, prod methods:Maturation on lees and Carbonic maceration.
        const isMaturationOnLeesOrCarbonicMaceration = !!wineProductionMethods?.some(
          (productionMethod) =>
            productionMethod?.name &&
            (productionMethod?.name?.toLowerCase() === PRODUCTION_METHOD_NAMES.MATURATION_ON_LEES ||
              productionMethod?.name?.toLowerCase() === PRODUCTION_METHOD_NAMES.CARBONIC_MACERATION)
        );
        const isWineMakingTechniqueGuide =
          post?.primary_tag?.slug === WINE_DETAIL_GUIDE_PRIMARY_TAGS.HOW_WINE_IS_MADE &&
          post?.slug === WINE_GUIDE_SLUGS.PRODUCTION_METHOD_LEES_AND_ORANGE;
        const shouldShowWineMakingTechniqueGuide =
          isWineMakingTechniqueGuide && (isWineTypeOrange || isMaturationOnLeesOrCarbonicMaceration);

        // Oak guide is shown for wines that have some or lots of oak.
        const isOakPostGuide =
          post?.primary_tag?.slug === WINE_DETAIL_GUIDE_PRIMARY_TAGS.HOW_WINE_IS_MADE &&
          post?.slug === WINE_GUIDE_SLUGS.OAK;
        const isOakLevelNone = wineOakLevel === WINE_OAK_LEVEL.NONE;
        const shouldDisplayOkaGuide = isOakPostGuide && !isOakLevelNone;

        if (shouldShowWineMakingTechniqueGuide || shouldDisplayOkaGuide) {
          cardTitle = t("winePages:wineDetailsCardTitle.prodMethods");
          filteredArticles.push({ ...post, cardTitle });
        }
      }
      return null;
    });

    return filteredArticles;
  };

  const filteredGuideArticles: GuidesArticlePost[] = (guideArticles && filterGuideArticles()) || [];
  const amountFilteredGuideArticles = filteredGuideArticles?.length ?? 0;

  if (amountFilteredGuideArticles < 1) {
    return null;
  }

  const getWineGuidesCardImageColor = (post: GuidesArticlePost): string => {
    const primaryTag = post?.primary_tag?.slug ?? "";
    const isWineClassRed = wineClassId === WINE_CLASS_IDS.DB_ID_WINE_CLASS_RED;
    const isWineClassWhite = wineClassId === WINE_CLASS_IDS.DB_ID_WINE_CLASS_WHITE;
    const isWineClassRose = wineClassId === WINE_CLASS_IDS.DB_ID_WINE_CLASS_ROSE;
    const isWineClassSparkling = wineClassId === WINE_CLASS_IDS.DB_ID_WINE_CLASS_SPARKLING;

    if (primaryTag === WINE_DETAIL_GUIDE_PRIMARY_TAGS.WINE_STYLE) {
      if (isWineTypeOrange) {
        return WINE_DETAIL_GUIDE_CARD_TAG_COLOR.WINE_TYPE_ORANGE;
      }
      if (isWineClassWhite) {
        return WINE_DETAIL_GUIDE_CARD_TAG_COLOR.WINE_STYLE_WHITE;
      }
      if (isWineClassRed) {
        return WINE_DETAIL_GUIDE_CARD_TAG_COLOR.WINE_STYLE_RED;
      }
      if (isWineClassRose) {
        return WINE_DETAIL_GUIDE_CARD_TAG_COLOR.WINE_STYLE_ROSE;
      }
      if (isWineClassSparkling) {
        return WINE_DETAIL_GUIDE_CARD_TAG_COLOR.WINE_STYLE_SPARKLING;
      }
      return colors.blueThin;
    }
    if (
      primaryTag === WINE_DETAIL_GUIDE_PRIMARY_TAGS.PRODUCTION_METHODS ||
      primaryTag === WINE_DETAIL_GUIDE_PRIMARY_TAGS.HOW_WINE_IS_MADE
    ) {
      return WINE_DETAIL_GUIDE_CARD_TAG_COLOR.PRODUCTION_METHOD;
    }
    if (primaryTag === WINE_DETAIL_GUIDE_PRIMARY_TAGS.RED_GRAPE_STYLE) {
      return WINE_DETAIL_GUIDE_CARD_TAG_COLOR.RED_GRAPE_STYLE;
    }
    if (primaryTag === WINE_DETAIL_GUIDE_PRIMARY_TAGS.WHITE_GRAPE_STYLE) {
      return WINE_DETAIL_GUIDE_CARD_TAG_COLOR.WHITE_GRAPE_STYLE;
    }
    return colors.blueThin;
  };

  const renderWineGuidesCard = (post: GuidesArticlePost, index: number): ReactElement => {
    const readingTimeLabel = t("winePages:postReadingTime", {
      minutes: post?.reading_time || 1,
    });
    const isImageInPost = !!post?.feature_image;
    const isProductionMethod = post?.cardTitle === GHOST_PRODUCTION_METHOD_CARD_TITLE;
    const imageSize = isProductionMethod ? GHOST_PRODUCTION_METHOD_IMAGE_SIZE : GHOST_IMAGE_SIZE;
    const postImage = isImageInPost ? generateImageUrl(post?.feature_image ?? "", imageSize) : blackUmbrella;
    const imageBackgroundColor = isImageInPost ? getWineGuidesCardImageColor(post) : colors.peach;
    return (
      <GhostArticlePostCard
        key={post?.title ?? ""}
        title={post?.title ?? ""}
        url={post?.canonical_url || post?.url || ""}
        cardTitle={post?.cardTitle}
        readingTimeLabel={readingTimeLabel}
        imageUrl={postImage}
        isLastPost={!!(index === (filteredGuideArticles?.length ?? 0) - 1)}
        shouldAddBorderToImageContainer={false}
        shouldAddBorderToLabelContainer
        cardWidth="255px"
        cardHeight="277px"
        imageWidth="256px"
        imageHeight="169px"
        imagePadding={isImageInPost ? "20px" : "50px"}
        imageObjectFit="contain"
        imageBackgroundColor={imageBackgroundColor}
      />
    );
  };

  const renderWineGuidesCards = (): ReactElement => (
    <>
      {filteredGuideArticles?.map((post: GuidesArticlePost, index: number) =>
        renderWineGuidesCard(post, index)
      )}
    </>
  );

  const renderNavigationBar = (): ReactElement => (
    <NavigationBarWrapper isWineDetailsPage={!!isWineDetailsPage}>
      {isWineDetailsPage && (
        <SeeAllLink to={URL_PATTERNS.GUIDE_101} target="_blank">
          {t("common:seeAll")}
        </SeeAllLink>
      )}
      <ArrowNavigator horizontalContainer={horizontalContainer} scrollAdjustment={0} />
    </NavigationBarWrapper>
  );

  return (
    <ContentContainer isWineDetailsPage={!!isWineDetailsPage}>
      <Header>{t("winePages:wineDetailsGuidesPostsTitle")}</Header>
      {renderNavigationBar()}
      <CardsContainer ref={horizontalContainer}>
        {isLoading ? <StyledLoading /> : renderWineGuidesCards()}
      </CardsContainer>
    </ContentContainer>
  );
};

export default GuidesRelatedToWineSection;
